// CreatorVideosPanel.tsx — vidéos d'un créateur, dans l'onglet Ressources de sa fiche
//
// Même présentation que l'onglet « Vidéos » du Dashboard (barre de filtres, bascule
// cartes / liste, mêmes cartes, même liste), limitée aux vidéos du créateur. Un clic sur la
// vignette ou sur « Lire » lance la vidéo dans une fenêtre, comme au Dashboard. Mêmes actions :
// Fiche (la fiche du module Médias), Désactiver / Rétablir (avec e-mail au créateur) et Relancer
// le transcodage.
// Données : admin-api DashboardLatest, paramètre ownerId (toutes ses vidéos, sans période).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import styles from './ChannelsSection.module.scss';
import tabs   from './DashboardTabs.module.scss';
import extra  from './VideosSection.module.scss';
import panels from './CreatorPanels.module.scss';
import dash   from './DashboardV2.module.scss';
import { ListTable, TitleCell, WhenCell, RowActions, Dash } from './DashboardList';
import { Segment, ViewToggle, useView, plural, size, clock } from './dashboardShared';
import { DashboardVideo, VideoState, Sort, SORTS, COMPARE, ORIGIN_LABEL, isNew, resolutionsText,
         StateBadge, Thumb, MiniThumb, Meta, Actions, AvailabilityDialog } from './VideosSection';
import VideoCommentsBar from './VideoCommentsBar';
import VideoPlayer from '../../widget/players/VideoPlayer';
import { API_URLS, WWW_URLS } from '../../../backendServices/urls';
import { getVerifiedMedia } from '../../../backendServices/mediaManagment/api';
import { getVideoById, getVideoList, retryVideoTranscoding, setVideoAvailability } from '../../../backendServices/mediaManagment/api_video';
import { useTypedSelector } from '../../../store/useTypeSelector';
import { RootState } from '../../../store/generalReducer';
import Box from '../../widget/windows/Box';
import VideoSheet2 from '../mediaManagment/VideoSheet2';
import { Video } from '../../../dataTypes/mediaManagment/video_types';

const VIEW_KEY = 'adminResources.videosView';

interface Props {
  adminId : number;
}

const CreatorVideosPanel: React.FC<Props> = ({ adminId }) => {

  const [ videos  , setVideos  ] = useState<DashboardVideo[]>([]);
  const [ loading , setLoading ] = useState(true);
  const [ error   , setError   ] = useState(false);
  const [ state   , setState   ] = useState<'all' | VideoState>('all');
  const [ universe, setUniverse ] = useState<string>('all');
  const [ query   , setQuery   ] = useState('');
  const [ sort    , setSort    ] = useState<Sort>('recent');
  const [ view    , setView    ] = useView(VIEW_KEY);
  const [ playing , setPlaying ] = useState<{ title: string; url: string } | null>(null);
  const [ playError , setPlayError ] = useState('');
  const [ reload  , setReload  ] = useState(0);
  const [ toggling, setToggling ] = useState<DashboardVideo | null>(null);
  const [ sheet   , setSheet   ] = useState<{ video: Video | null; error: string } | null>(null);
  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const me = authentication.admin?.id;

  useEffect(() => {
    let cancelled = false;
    if (reload === 0) setLoading(true);
    setError(false);
    axios.get(API_URLS.getDashboardLatest, { params: { ownerId: adminId, limit: 1000, videoLimit: 1000 } }).then(({ data }) => {
      if (cancelled) return;
      setVideos((data.videos ?? []).map((x: any): DashboardVideo => ({
        id: x.id, featuredUntil: x.featuredUntil ?? null, name: x.name || '', publicRef: x.publicRef || '', hasThumbnail: !!x.hasThumbnail,
        creationDate: x.creationDate ?? null, owner: x.owner || '', ownerEmail: x.ownerEmail || '',
        state: x.state || 'ready', progress: x.progress ?? null, durationSeconds: x.durationSeconds ?? null,
        active: x.active ?? true, publicPortal: !!x.publicPortal, views: Number(x.views ?? 0),
        resolutions: x.resolutions || '', social: Array.isArray(x.social) ? x.social : [],
        universe: x.universe ?? null, origin: x.origin || 'regie', sizeBytes: Number(x.sizeBytes ?? 0),
        geo: x.geo ?? null,
      })));
    }).catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [adminId, reload]);

  // Même adresse de lecture que le Dashboard et la liste des médias (qualité 001).
  const play = useCallback(async (v: DashboardVideo) => {
    if (!v.publicRef) return;
    setPlayError('');
    const url = await getVerifiedMedia([`${WWW_URLS.MediaServer}${v.publicRef}/videos/001/index.m3u8`]);
    if (url) setPlaying({ title: v.name, url });
    else { setPlaying({ title: v.name, url: '' }); setPlayError('Vidéo indisponible (encore en transcodage ?).'); }
  }, []);

  // Les univers (catégories) présents, les plus fréquents d'abord (5 au plus), comme au Dashboard.
  const universes = useMemo(() => {
    const count = new Map<string, number>();
    videos.forEach(v => { if (v.universe) count.set(v.universe, (count.get(v.universe) ?? 0) + 1); });
    return Array.from(count.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([u]) => u);
  }, [videos]);

  const matching = useMemo(() => {
    const q = query.trim().toLowerCase();
    return videos.filter(v => (universe === 'all' || v.universe === universe) && (!q || `${v.name} ${v.universe ?? ''}`.toLowerCase().includes(q)));
  }, [videos, universe, query]);

  const shown = useMemo(() => matching.filter(v => state === 'all' || v.state === state).sort(COMPARE[sort]), [matching, state, sort]);

  const counts = useMemo(() => {
    const c: Record<'all' | VideoState, number> = { all: matching.length, transcoding: 0, ready: 0, hidden: 0, failed: 0 };
    matching.forEach(v => { c[v.state]++; });
    return c;
  }, [matching]);

  // Relance du transcodage, désactivation / rétablissement et fiche : comme au Dashboard (DashboardV2).
  const retry = useCallback(async (v: DashboardVideo) => {
    const res = await retryVideoTranscoding(v.id, me);
    if (res.code === 1) setReload(n => n + 1);
    return res.code === 1;
  }, [me]);

  const availability = useCallback(async (v: DashboardVideo, enabled: boolean, portal: boolean, comment: string): Promise<{ error: string; notice: string }> => {
    if (!me) return { error: 'Session expirée, reconnectez-vous.', notice: '' };
    const res = await setVideoAvailability(me, v.id, enabled, portal, comment);
    if (res.ok) setReload(n => n + 1);
    const mail   = res.email;
    const notice = !res.ok ? '' : !mail ? '' : mail.sent ? `Un e-mail a été envoyé au créateur (${mail.to}).` : `L'e-mail au créateur n'a pas pu être envoyé : ${mail.error}.`;
    return { error: res.error, notice };
  }, [me]);

  const openSheet = useCallback(async (v: DashboardVideo) => {
    setSheet({ video: null, error: '' });
    const one = await getVideoList(false, undefined, v.id);
    const found = one.code === 1 ? one.payload.find(x => x.id === v.id) : undefined;
    const res = found ? { code: 1, payload: found } as const : await getVideoById(v.id);
    setSheet(res.code === 1 ? { video: res.payload, error: '' } : { video: null, error: 'Impossible de charger la fiche de cette vidéo.' });
  }, []);

  const actions = (v: DashboardVideo, playIcon = false) =>
    <Actions v={v} onPlay={play} onSheet={openSheet} onRetry={retry} onToggle={setToggling} feature={me ? { adminId: me, onChanged: () => setReload(n => n + 1) } : undefined} playIcon={playIcon} />;

  if (loading) return <span>Chargement des vidéos…</span>;
  if (error)   return <div className={styles.empty}>Chargement impossible.</div>;
  if (videos.length === 0) return <div className={styles.empty}>Aucune vidéo.</div>;

  return (
    <div>
      <div className={styles.toolbar} style={{ marginBottom: 16 }}>
        <div className={extra.search}>
          <svg className={extra.searchIcon} width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="7" cy="7" r="5"/><path d="M11 11l4 4"/></svg>
          <input className={extra.searchInput} type="text" value={query} placeholder="Titre…" aria-label="Rechercher une vidéo"
                 onChange={e => setQuery(e.target.value)} />
          {query && <button type="button" className={extra.searchClear} onClick={() => setQuery('')} aria-label="Effacer la recherche">✕</button>}
        </div>
        <Segment label="État" value={state} onChange={setState}
                 choices={[ ['all', `Toutes ${counts.all}`], ['transcoding', `En transcodage ${counts.transcoding}`], ['ready', `Visibles ${counts.ready}`],
                            ['hidden', `Masquées ${counts.hidden}`], ['failed', `En échec ${counts.failed}`] ] as const} />
        {universes.length > 1 &&
          <Segment label="Univers" value={universe} onChange={setUniverse}
                   choices={[ ['all', 'Tous les univers'] as const, ...universes.map(u => [u, u] as const) ]} />}
        {(query.trim() !== '' || state !== 'all' || universe !== 'all') && <>
          <span className={extra.count} aria-live="polite"><strong>{plural(shown.length, 'vidéo')}</strong> sur {videos.length}</span>
          <button type="button" className={extra.reset} onClick={() => { setQuery(''); setState('all'); setUniverse('all'); }}>Réinitialiser</button>
        </>}
        <span className={styles.spacer}/>
        <label className={extra.sort}>Trier par
          <select value={sort} onChange={e => setSort(e.target.value as Sort)}>
            {SORTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {shown.length === 0 && <div className={styles.empty}>Aucune vidéo ne correspond.</div>}

      {view === 'cards' && shown.length > 0 && (
        <div className={styles.cards}>
          {shown.map(v => (
            <article key={v.id} className={`${styles.card} ${isNew(v) ? styles.cardNew : ''} ${v.state === 'failed' ? tabs.cardWarn : ''}`}>
              <Thumb v={v} onPlay={play} />
              <div className={styles.cardBody}>
                <span className={styles.cardName} title={v.name}>{v.name}</span>
                <Meta v={v} />
                <div className={styles.chips}>
                  {v.universe && <span>{v.universe}</span>}
                  <span>{ORIGIN_LABEL[v.origin]}</span>
                </div>
                {actions(v)}
              </div>
            </article>
          ))}
        </div>
      )}

      {view === 'list' && shown.length > 0 && (
        <ListTable items={shown} rowKey={v => v.id} dateOf={v => v.creationDate}
                   noun={{ one: 'vidéo', many: 'vidéos' }}
                   sort={sort === 'recent' ? null : sort} onSort={id => setSort((id ?? 'recent') as Sort)}
                   columns={[
          { id: 'video', label: 'Vidéo', cell: v =>
              <TitleCell thumb={<MiniThumb v={v} />} title={v.name} titleHint={v.name}
                         sub={[ v.universe, ORIGIN_LABEL[v.origin], resolutionsText(v.resolutions) ].filter(Boolean).join(' · ')} /> },
          { id: 'state', label: 'État', cell: v => <StateBadge v={v} /> },
          { id: 'duration', label: 'Durée', right: true, sort: COMPARE.duration,
            cell: v => v.state === 'failed' || !clock(v.durationSeconds) ? <Dash/> : clock(v.durationSeconds) },
          { id: 'views', label: 'Vues', right: true, sort: COMPARE.views,
            cell: v => v.state === 'ready' || v.state === 'hidden' ? v.views.toLocaleString('fr-FR') : <Dash/> },
          { id: 'size', label: 'Taille', right: true, hide: 'md', sort: COMPARE.size, cell: v => size(v.sizeBytes) || <Dash/> },
          { id: 'date', label: 'Ajoutée', cell: v => <WhenCell iso={v.creationDate} /> },
          { id: 'actions', label: '', right: true, cell: v => <div className={panels.always}><RowActions>{actions(v, true)}</RowActions></div> },
        ]} />
      )}

      {toggling && <AvailabilityDialog video={toggling} adminId={me} onClose={() => setToggling(null)} onConfirm={availability} />}

      {sheet && (
        <Box width="95vw" height="95vh" onClose={() => setSheet(null)} logo={true}>
          {sheet.video
            ? <>
                <VideoCommentsBar videoId={sheet.video.id} adminId={me} />
                <VideoSheet2 video={sheet.video} ownerAdminId={adminId} onEdit={() => { setSheet(null); setReload(n => n + 1); }} />
              </>
            : <div className={styles.empty}>{sheet.error || 'Chargement de la fiche…'}</div>}
        </Box>
      )}

      {playing && (
        <div className={dash.overlay} onClick={() => setPlaying(null)}>
          <div className={dash.modal} onClick={e => e.stopPropagation()}>
            <div className={dash.sectionHeader}>
              <span>{playing.title}</span>
              <button className={dash.refresh} onClick={() => setPlaying(null)} title="Fermer">✕</button>
            </div>
            {playing.url && <VideoPlayer url={playing.url}
                                         onError={e => { if (e.fatal) setPlayError('Lecture impossible.'); }}
                                         onReady={() => setPlayError('')} />}
            {playError && <div className={styles.empty}>{playError}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorVideosPanel;
